import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { showSuccess, showError } from '@/components/common/Toast';

interface BulkImportResult {
  success: number;
  errors: Array<{ row: number; error: string }>;
}

interface CustomerImportRow {
  name: string;
  segment?: 'Enterprise' | 'Mid-Market' | 'SMB' | 'Public Sector';
  tier?: 'Strategic' | 'Key' | 'Standard';
  region?: string;
  website?: string;
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  notes?: string;
}

interface OpportunityImportRow {
  name: string;
  customerId: string;
  salesOrg?: string;
  opportunityOwner?: string;
  assignedArchitect?: string;
  probability?: number;
  notes?: string;
  forecastCloseDate?: string;
}

/**
 * Bulk import customers
 */
export function useBulkImportCustomers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customers: CustomerImportRow[]) =>
      api.post<BulkImportResult>('/customers/bulk', { customers }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      if (data.errors.length === 0) {
        showSuccess(`Successfully imported ${data.success} customers`);
      } else {
        showError(
          `Imported ${data.success} customers`,
          `${data.errors.length} rows had errors`
        );
      }
    },
  });
}

/**
 * Bulk import opportunities
 */
export function useBulkImportOpportunities() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (opportunities: OpportunityImportRow[]) =>
      api.post<BulkImportResult>('/opportunities/bulk', { opportunities }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
      if (data.errors.length === 0) {
        showSuccess(`Successfully imported ${data.success} opportunities`);
      } else {
        showError(
          `Imported ${data.success} opportunities`,
          `${data.errors.length} rows had errors`
        );
      }
    },
  });
}

/**
 * Get export URL for customers
 */
export function getCustomersExportUrl(): string {
  return '/api/customers/export';
}

/**
 * Get export URL for opportunities
 */
export function getOpportunitiesExportUrl(): string {
  return '/api/opportunities/export';
}

/**
 * Parse CSV string into rows
 */
export function parseCSV(csvContent: string): string[][] {
  const lines = csvContent.split('\n');
  const result: string[][] = [];

  for (const line of lines) {
    if (!line.trim()) continue;

    const row: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (inQuotes) {
        if (char === '"' && nextChar === '"') {
          current += '"';
          i++; // Skip next quote
        } else if (char === '"') {
          inQuotes = false;
        } else {
          current += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ',') {
          row.push(current);
          current = '';
        } else {
          current += char;
        }
      }
    }
    row.push(current);
    result.push(row);
  }

  return result;
}

/**
 * Convert CSV rows to customer import format
 */
export function csvToCustomers(
  rows: string[][],
  columnMapping: Record<string, number>
): CustomerImportRow[] {
  const { name, segment, tier, region, website, primaryContactName, primaryContactEmail, primaryContactPhone, notes } = columnMapping;

  return rows.map((row) => ({
    name: name !== undefined ? row[name]?.trim() || '' : '',
    segment: segment !== undefined ? (row[segment]?.trim() as CustomerImportRow['segment']) || undefined : undefined,
    tier: tier !== undefined ? (row[tier]?.trim() as CustomerImportRow['tier']) || undefined : undefined,
    region: region !== undefined ? row[region]?.trim() || undefined : undefined,
    website: website !== undefined ? row[website]?.trim() || undefined : undefined,
    primaryContactName: primaryContactName !== undefined ? row[primaryContactName]?.trim() || undefined : undefined,
    primaryContactEmail: primaryContactEmail !== undefined ? row[primaryContactEmail]?.trim() || undefined : undefined,
    primaryContactPhone: primaryContactPhone !== undefined ? row[primaryContactPhone]?.trim() || undefined : undefined,
    notes: notes !== undefined ? row[notes]?.trim() || undefined : undefined,
  })).filter((c) => c.name); // Filter out rows without name
}

/**
 * Convert CSV rows to opportunity import format
 */
export function csvToOpportunities(
  rows: string[][],
  columnMapping: Record<string, number>
): OpportunityImportRow[] {
  const { name, customerId, salesOrg, opportunityOwner, assignedArchitect, probability, notes, forecastCloseDate } = columnMapping;

  return rows.map((row) => ({
    name: name !== undefined ? row[name]?.trim() || '' : '',
    customerId: customerId !== undefined ? row[customerId]?.trim() || '' : '',
    salesOrg: salesOrg !== undefined ? row[salesOrg]?.trim() || undefined : undefined,
    opportunityOwner: opportunityOwner !== undefined ? row[opportunityOwner]?.trim() || undefined : undefined,
    assignedArchitect: assignedArchitect !== undefined ? row[assignedArchitect]?.trim() || undefined : undefined,
    probability: probability !== undefined && row[probability] ? parseInt(row[probability], 10) : undefined,
    notes: notes !== undefined ? row[notes]?.trim() || undefined : undefined,
    forecastCloseDate: forecastCloseDate !== undefined ? row[forecastCloseDate]?.trim() || undefined : undefined,
  })).filter((o) => o.name && o.customerId); // Filter out rows without name or customerId
}
